import { useEffect } from "react";

const DEFAULT_OG_IMAGE = "https://flowgantt.eu/og-image.png";
const SUPPORTED_LOCALES = ["fr", "en", "es", "de", "it", "pt", "nl", "zh", "ja", "vi"];

/**
 * Lightweight per-route SEO manager. No external deps.
 * Manages <title>, meta description/keywords, canonical, Open Graph, Twitter,
 * robots, hreflang alternates and structured-data on a per-page basis.
 *
 * All arguments are optional. If a value is not provided, the field is left
 * untouched so the global values from public/index.html continue to apply.
 *
 * @param {Object} opts
 * @param {string} [opts.title]        Full <title> content.
 * @param {string} [opts.description]  Meta description + OG description.
 * @param {string} [opts.canonical]    Absolute canonical URL for this page.
 * @param {string} [opts.image]        Absolute URL of the OG image.
 * @param {string} [opts.type]         og:type — "website" (default) or "article".
 * @param {boolean} [opts.noindex]     Emit `noindex, nofollow` for private pages.
 * @param {string[]} [opts.locales]    List of language codes for hreflang alternates.
 * @param {string} [opts.hreflangBase] Absolute base URL to build hreflang alternates from.
 */
export default function usePageMeta({
  title,
  description,
  canonical,
  image,
  type = "website",
  noindex = false,
  locales,
  hreflangBase,
} = {}) {
  useEffect(() => {
    if (title) {
      document.title = title;
    }

    const setMeta = (selector, attr, value) => {
      if (!value) return;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [name, val] = selector
          .replace(/^meta\[/, "")
          .replace(/\]$/, "")
          .split("=");
        el.setAttribute(name, val.replace(/^["']|["']$/g, ""));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:type"]', "content", type);
    setMeta('meta[property="og:image"]', "content", image || DEFAULT_OG_IMAGE);
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", description);
    setMeta('meta[name="twitter:image"]', "content", image || DEFAULT_OG_IMAGE);

    // Canonical URL. Sur une page traduite (hreflangBase), /?lang=en est une page à part entière, déclarée en
    // hreflang : son canonical pointe sur elle-même, sinon Google ignore les alternatives.
    const urlLang = new URLSearchParams(window.location.search).get("lang");
    const localized = hreflangBase && urlLang && urlLang !== "fr" && SUPPORTED_LOCALES.includes(urlLang);
    const canonicalUrl = localized
      ? `${hreflangBase}${hreflangBase.includes("?") ? "&" : "?"}lang=${urlLang}`
      : canonical;
    if (canonicalUrl) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonicalUrl);
      setMeta('meta[property="og:url"]', "content", canonicalUrl);
    }

    // Hreflang alternates — build absolute URLs for every supported locale.
    // Previous dynamic links (marked data-i18n-alt="true", also the ones pre-rendered at build time) are always
    // dropped first, so a page without alternates (e.g. the French-only blog) never inherits another page's.
    document.head
      .querySelectorAll('link[rel="alternate"][data-i18n-alt="true"]')
      .forEach((n) => n.remove());
    if (hreflangBase) {
      const langs = Array.isArray(locales) && locales.length ? locales : SUPPORTED_LOCALES;
      langs.forEach((code) => {
        const l = document.createElement("link");
        l.setAttribute("rel", "alternate");
        l.setAttribute("hreflang", code);
        l.setAttribute("data-i18n-alt", "true");
        const url = code === "fr"
          ? hreflangBase
          : `${hreflangBase}${hreflangBase.includes("?") ? "&" : "?"}lang=${code}`;
        l.setAttribute("href", url);
        document.head.appendChild(l);
      });
      const xdef = document.createElement("link");
      xdef.setAttribute("rel", "alternate");
      xdef.setAttribute("hreflang", "x-default");
      xdef.setAttribute("data-i18n-alt", "true");
      xdef.setAttribute("href", hreflangBase);
      document.head.appendChild(xdef);
    }

    // Robots
    const robots = noindex
      ? "noindex, nofollow"
      : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
    let rEl = document.head.querySelector('meta[name="robots"]');
    if (!rEl) {
      rEl = document.createElement("meta");
      rEl.setAttribute("name", "robots");
      document.head.appendChild(rEl);
    }
    rEl.setAttribute("content", robots);
  }, [title, description, canonical, image, type, noindex, locales, hreflangBase]);
}
